<template>
  <div>
  <h2 class="mb-2 mt-2"><span id="link-index-certificate-program" class="page-heading">Certificate Program</span></h2>
  <div class="block">
    <div class="block-header">
      <ul class="go-to-links">
        <li @click.prevent="goTo('purpose')">PURPOSE</li>
        <li @click.prevent="goTo('scope')">SCOPE</li>
        <li @click.prevent="goTo('learning_outcomes')">LEARNING OUTCOMES</li>
        <li @click.prevent="goTo('participation_requirements')">PARTICIPATION REQUIREMENTS</li>
        <li @click.prevent="goTo('post_completion_activities')">POST COMPLETION ACTIVITIES</li>
        <li @click.prevent="goTo('support_requests')">SUPPORT REQUESTS</li>
        <li @click.prevent="goTo('fees_refunds_and_other_policies')">FEES REFUNDS AND OTHER POLICIES</li>
        <li @click.prevent="goTo('general_program_notifications')">GENERAL PROGRAM NOTIFICATIONS</li>
      </ul>
    </div>

    <p id="purpose" class="font-weight-bold mb-1 mt-3">PURPOSE</p>
    <p>
      The objective of this certificate program is to instruct, assess, and issue a program certificate to an
      employee (or employee candidate) of cannabis cultivation, testing, manufacturing, or dispensary facilities.
    </p>

    <p id="scope" class="font-weight-bold mb-1 mt-3">SCOPE</p>
    <p>
      The program scope covers the following topical areas, as appropriate for cannabis facility staff.
    </p>

    <ul>
      <li>Quality Management Systems (QMS) and operational programs.</li>
      <li>Key QMS staff roles and responsibilities, including training, health & safety, hazard control, tracking and recall, transportation, security, customer and patient controls, diversion prevention, and cash sales.</li>
      <li>Cannabis products and effects, including compounds, potency, and at-risk groups.</li>
      <li>Introduction to cannabis history, laws, and common licensing requirements.</li>
      <li>Safe product handling including contamination risk, handling, glove use, hygiene, cleaning and sanitizing.</li>
    </ul>

    <p id="learning_outcomes" class="font-weight-bold mb-1 mt-3">LEARNING OUTCOMES</p>
    <p>At the completion of the program you will be able to demonstrate cannabis-industry knowledge and skill in these topical areas.</p>

    <ul>
      <li>Expertise with FOCUS Industry Standards and knowledge of a Quality Management System (QMS).</li>
      <li>Understanding of key operational programs including training, health & safety, hazard control, tracking and recall, transportation, security, customer and patient controls, diversion prevention, and cash sales.</li>
      <li>Basic awareness of the federal, state, and local laws that support cannabis-facility licensing.</li>
      <li>Readiness to act in an operational role, as directed by cannabis facility management.</li>
      <li>Knowledge of cannabis plants, compounds, products, use, and handling.</li>
    </ul>

    <p id="participation_requirements" class="font-weight-bold mb-1 mt-3">PARTICIPATION REQUIREMENTS</p>
    <ol>
      <li>
        The certificate program is self-guided, with completion steps and instructions built into the application. There are no instructional personnel.
      </li>
      <li>
        Here is what you need to use the certificate program.
        <ul>
          <li>Access to a web browser, an iPhone, iPad, or Android phone or tablet.</li>
          <li>A valid email address, mailing address, and phone number so you can register with the program and authenticate your identity and residence.</li>
          <li>Payment of the program license fee (Levels 1 & 2 are a free trial). Payment is made within the certificate program app, after you register and sign in.</li>
          <li>A minimum ‘seat time’ of approximately 5 hours to LEARN, QUIZ, and complete the EXAM. There is no time limit on how quickly you finish the program. Some students may take longer than others. Learn at your own pace and convenience.</li>
          <li>The program is only available in English language.</li>
        </ul>
      </li>
    </ol>

    <p id="program_steps_to_completion" class="font-weight-bold mb-1 mt-3">PROGRAM STEPS TO COMPLETION</p>
    <p>All program study levels must be completed. There is no provision for partial work or previous experience.</p>

    <ul>
      <li>Access the certificate program
        <ul>
          <li>From a browser go to <a href="https://rca.cogneti.com" target="_blank">https://rca.cogneti.com</a></li>
          <li>From an iPhone, iPad, or Android device download ‘Responsible Cannabis Agent‘ from the App Store or Google Play Store</li>
        </ul>
      </li>
      <li>
        Use the LEARN function to begin reading your way through the levels of course instruction.
      </li>
      <li>
        Complete at least one ten-question QUIZ for each level of instruction. Practice your recall by taking a quiz several times to increase your recall and skill.
      </li>
      <li>
        Once you have read and quizzed at all levels the EXAM is available for you to attempt.
      </li>
      <li>
        Pass the exam with at least a 70% score.
      </li>
      <li>
        If you do not pass an exam, the app will show where you need to study by marking that instructional level in red. You must complete additional quizzes on that level before the exam will unlock for another attempt. You will receive an email detailing your exam performance and identifying high learning needs.
      </li>
      <li>
        When you PASS the exam, you will be emailed a program certificate that has a unique ID number and term of validity, along with an eBadge. The eBadge works as a handy and portable form of showing your accomplishment and program expertise.
        The eBadge unique number lets you or employers validate your certificate at <a target="_blank" href="https://admin.cogneti.com/badge">cogneti.com/badge</a>.
      </li>
    </ul>

    <p id="support_requests" class="font-weight-bold mb-1 mt-3">SUPPORT REQUESTS</p>
    <p>
      Support may be requested by sending an email to <a href="mailto:support@cogneti.com">support@cogneti.com</a>.
      Support staff will respond within one business day.
    </p>

    <p id="post_completion_activities" class="font-weight-bold mb-1 mt-3">POST-COMPLETION ACTIVITIES</p>
    <ul>
      <li><strong>Issued Certificate:</strong> When you pass the exam, your course certificate is issued. We send it to the email address you registered when you began the program. It identifies you by name and shows an expiration date. You receive a unique badge ID, registered to your email and user name.
        The ID allows you to validate your certificate at <a target="_blank" href="https://admin.cogneti.com/badge">cogneti.com/badge</a>.
      </li>
      <li><strong>eBadge:</strong> Once you receive your ebadge, you can email it to employers, print a paper copy, or order a laminated copy with your photograph (about the size of a credit card). The eBadge serves as portable evidence of the program certificate. The eBadge is sent to your email address. Save it.</li>
      <li><strong>Re-Issue Course Certificate:</strong> The program certificate expires after the term of validity. You may obtain a re-issued course certificate, by completing all steps of the program again. You must  complete the quizzes on all levels and pass the exam.</li>
      <li><strong>Practice and Skills Maintenance:</strong> The certificate application (mobile app) is a tool to practice and maintain your skills, any time and any place. The app is a reference for you to use and review.</li>
    </ul>

    <p id="fees_refunds_and_other_policies" class="font-weight-bold mb-1 mt-3">FEES, REFUNDS, AND OTHER POLICIES</p>
    <ul>
      <li>The certificate program license fee is $189, payable within the application.</li>
      <li>There are no deadlines for completion of the program.</li>
      <li>Additional exam attempts are available, once quizzes are again completed for the instructional level tagged as HIGH LEARNING NEED. No additional fees are required for additional exam attempts.</li>
      <li>All license-fee sales are final. Refunds may be requested by issuing a support request using the mobile app. Consideration of the refund request will be made solely at the discretion of Cogneti staff.</li>
    </ul>

    <p id="general_program_notifications" class="font-weight-bold mb-1 mt-3">GENERAL PROGRAM NOTIFICATIONS</p>
    <ul>
      <li>Cogneti, LLC does not receive any commercial or third-party support in the creation of the SFH certificate program. The awarding of a certificate is based solely on the individual performance of the student.</li>
      <li>At completion of the certificate program, certificate holders may be expected to demonstrate the learning objectives shown above. No other inferences may be made or implied.</li>
      <li>The program content is up to date as of the 2022 publication of FOCUS Standards.</li>
      <li>Per Cogneti Privacy Policy, personal identification information (names, email, addresses, telephone numbers), communications, and training records provided by program participants are held confidential. Certain information (name, email, phone number, and program status) is made available to an employer when approved by the program participant. Personal identification information will not be provided or sold to advertisers or third-parties (except  by lawful court order).</li>
      <li>No academic or continuing education credit is earned by completion of this certificate program.</li>
    </ul>

    <p class="pt-5">
      (Reference CAP 5.2.1 – 5.2.1.3 & 5.5 – 5.5.2.1 & 5.6 – 5.6.3 & 6.1.3 – 6.1.3.4 & 6.1.4 – 6.1.4.2)
    </p>

  </div>
  </div>
</template>
<script>
export default {
  name: 'Program63'
}
</script>



// WEBPACK FOOTER //
// src/components/views/guest/CertificateProgram/Program63.vue